import { isEqual } from 'lodash';
import stampit from 'stampit';
import { RootScopeEvents } from '../../../common/services/root-scope-types';
import { shareWithApollo } from '../../../utility/shareWithApollo';

export default angular
  .module('doxy.common.components.message-chat.TextChatRealtimeComponent', [])
  .factory(
    'TextChatRealtimeComponent',
    function (
      RealtimeComponent,
      $timeout,
      notificationFactory,
      pn,
      globalUi,
      Browser,
      intercomFactory,
      VideoProviderFactory,
      $rootScope,
      currentUserHelper
    ) {
      function _updateHistory({ uid, content, author }) {
        this.getHistory(uid).messages.push({
          date: new Date(),
          author,
          content,
        });
      }

      return stampit({
        deepProps: {
          history: {},
          emptyChatTitle: true,
          target: null,
          minimized: true,
          _callBackOnLeave: [],
          newMessagesCounter: {},
          componentName: 'chat',
          events: {
            message: 'onMessageEvent',
          },
        },
        init(opts) {
          Object.assign(this, opts);

          $rootScope.$on(
            RootScopeEvents.ACTIVE_PROVIDER.JOIN_SESSION_SUCCESS,
            (_) => {
              const noChatStarted = !Object.keys(this.history).find(
                (key) => this.history[key].messages.length > 0
              );
              if (noChatStarted && !pn.isDoctor()) {
                const { person: currentDoctorPerson } =
                  VideoProviderFactory.activeProvider.connections.find((conn) =>
                    conn.person.isDoctor()
                  ) || {};
                if (currentDoctorPerson) {
                  this.updateTarget(currentDoctorPerson);
                }
              }
            }
          );
        },
        methods: {
          initHistory() {
            if (Boolean(currentUserHelper.getUserType())) {
              const legacySession = shareWithApollo.getDataForLegacy();
              this.history = legacySession.chatHistory ?? {};
              const person = this.pn.allJoinedPeople.findByUid(
                legacySession.previousCall?.selectedChatUserIdForDashboard
              );
              if (
                this.history[pn.getPersonUserId(person)]?.messages.length > 0
              ) {
                this.emptyChatTitle = false;
                this.updateTarget(person);
                this.toggleChat(true);
              }
            }
          },
          getUserId() {
            return pn.getUserId() ?? String(currentUserHelper.user?.id);
          },
          getHistory(uid) {
            this.history[uid] = this.history[uid] || {
              messages: [],
              newMessages: false,
              newMessagesCounter: 0,
            };
            return this.history[uid];
          },

          getCurrentPersonMessages() {
            const history = this.getHistory(pn.getPersonUserId(this.target));
            return history.messages;
          },

          onPnUnsubscribe() {
            this.clearState();
          },

          clearState() {
            $timeout(() => {
              this.minimized = true;
              this.updateTarget(null);
              this.history = {};
            });
          },

          close() {
            this.updateTarget(null);
          },

          /**
           * @description all history between calls should be kept regardless of the user's role
           *
           * @returns void
           */
          _onCallHold() {
            return undefined;
          },

          _onCallEnd() {
            return undefined;
          },

          reset() {
            if (pn.isDoctor()) {
              // should keep all history and do not close;
              return;
            }

            this.clearState();
            this.toggleChat(false);
          },

          setCallbackOnLeave(callback) {
            // Used for cleanup in text-chat component's textarea.
            if (typeof callback !== 'function') {
              return;
            }
            this._callBackOnLeave.push(callback);
          },

          _onLeave(person) {
            $timeout(() => {
              // we do not need to all history in case if person is current
              // delete this.history[person.uuid]
              delete this.newMessagesCounter[person.uuid];
              const history = this.getHistory(person.uuid);
              history.newMessagesCounter = 0;
              const isThisChatWithPersonWhoLeft = isEqual(person, this.target);
              globalUi.set({
                newMessages: this.countAllUnreadMessages(),
              });
              if (
                Boolean(pn.isDoctor()) &&
                isThisChatWithPersonWhoLeft &&
                history.messages.length === 0 &&
                !Boolean(VideoProviderFactory.activeProvider.isInCall)
              ) {
                const patients = pn.people.getPatients();
                this.close();

                if (patients.length > 0) {
                  this.openChat(patients[0], false);
                }
              }
            });
          },

          onMessageEvent(content, person, envelope = {}) {
            $rootScope.$emit(RootScopeEvents.CHAT.ON_MESSAGE, person);
            const uid = pn.getPersonUserId(person);

            const author = {
              uid,
              userPicture: person.userPicture,
            };

            _updateHistory.call(this, { uid, content, author });
            const history = this.getHistory(uid);

            if (Boolean(this.minimized) && !isEqual(this.target, person)) {
              this.updateTarget(person);
            }

            notificationFactory.messagesSound();
            this.emptyChatTitle = false;

            const { requestAck, messageId } = envelope;
            if (requestAck != null) {
              this.sendChatAck(messageId);
            }

            $timeout(() => {
              history.newMessages =
                Boolean(this.minimized) || !isEqual(this.target, person);

              if (history.newMessages) {
                this.newMessagesCounter = Object.assign(
                  this.newMessagesCounter || {},
                  {
                    [uid]: this.newMessagesCounter[uid]
                      ? this.newMessagesCounter[uid] + 1
                      : 1,
                  }
                );
                history.newMessagesCounter =
                  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                  (history.newMessagesCounter ?? 0) + 1;

                // Always show chat on new message except for if it's a doctor on mobile
                // or intercom widget is open
                const isMobileDoctor =
                  pn.isDoctor() && Browser.isMobileScreen();
                if (isMobileDoctor || intercomFactory.isVisible()) {
                  // Only update the counter
                  globalUi.set({
                    newMessages: this.countAllUnreadMessages(),
                  });
                } else {
                  this.toggleChat(true);
                }
              }
            });
          },

          countAllUnreadMessages() {
            let sum = 0;
            for (const uid of Object.keys(this.newMessagesCounter)) {
              sum += this.newMessagesCounter[uid];
            }
            return sum;
          },

          checkTarget() {
            const userTargetOnline = pn.people.find(
              (p) => pn.getPersonUserId(p) === pn.getPersonUserId(this.target)
            )[0];

            if (!Boolean(userTargetOnline)) {
              console.warn('chat:sendMessage: person is offline');
              // Happened during patient goes offline / reinit check-in while chatting.
              return false;
            }
            if (this.target !== userTargetOnline) {
              this.target = userTargetOnline;
            }
            return true;
          },

          sendMessage(content, person) {
            const uid = pn.getPersonUserId(person);
            const author = {
              uid: this.getUserId(),
              userPicture: pn.userPicture,
            };
            _updateHistory.call(this, { uid, content, author });

            if (!Boolean(this.checkTarget())) {
              return;
            }
            this.emptyChatTitle = false;
            this.send(this.target.uuid, 'message', content);
          },

          sendIsTyping(isTyping) {
            if (!Boolean(this.checkTarget())) {
              return;
            }
            this.send(this.target.uuid, 'isTyping', { isTyping });
          },

          sendChatAck(messageId) {
            if (!Boolean(this.checkTarget()) && messageId != null) {
              return;
            }
            this.send(this.target.uuid, 'ack', { messageId });
          },

          toggleChat(condition = this.minimized) {
            const person = this.target;
            if (!person) {
              return;
            }

            if (condition) {
              $rootScope.$emit('textChat:show');
              document.querySelector('body').classList.add('chat-opened');
            } else {
              document.querySelector('body').classList.remove('chat-opened');
            }

            if (!Browser.isMobile) {
              $timeout(() => {
                const element = document.querySelector('.chat-form textarea');

                if (element) {
                  element[condition ? 'focus' : 'blur']();
                }
              });
            }

            this.minimized = !condition;
            this.getHistory(person.uuid).newMessages = false;
            this.getHistory(person.uuid).newMessagesCounter = 0;

            this.newMessagesCounter = Object.assign(
              this.newMessagesCounter || {},
              {
                [person.uuid]: 0,
              }
            );
            globalUi.set({
              newMessages: this.countAllUnreadMessages(),
            });
          },

          canChat(person = this.target) {
            if (!Boolean(person)) {
              return false;
            }
            if (Boolean(this.history[pn.getPersonUserId(person)])) {
              return true;
            }
            if (!Boolean(person.state.doNotDisturbMode)) {
              return true;
            }
            if (Boolean(VideoProviderFactory.activeProvider.isInCall)) {
              return true;
            }
            return false;
          },

          updateTarget(person) {
            this.target = person;
            $rootScope.$emit(RootScopeEvents.CHAT.ON_UPDATE_PERSON);
          },

          isOpen() {
            return Boolean(this.target);
          },

          openChatByPersonUuid(uuid) {
            const person = this.pn.allJoinedPeople.findByUid(uuid);
            this.openChat(person);
          },

          openChat(person, condition = true) {
            if (!Boolean(this.canChat(person))) {
              return;
            }
            this.emptyChatTitle =
              this.getHistory(pn.getPersonUserId(person)).messages.length === 0;
            const isThisChatWithSamePerson = isEqual(person, this.target);
            if (!isThisChatWithSamePerson) {
              for (const item of this._callBackOnLeave) {
                item();
              }
            }
            this.updateTarget(person);
            this.toggleChat(condition);
            this.hideSidebar();
          },

          hideSidebar() {
            if (Browser.isMobileScreen()) {
              globalUi.toggleSidebar(true);
            }
          },

          isCurrentChatAvailable() {
            return pn.isPersonOnline(this.target);
          },
        },
      }).compose(RealtimeComponent);
    }
  ).name;
